/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { PCView, SPView } from "components/common/MediaQuery";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { MobileBreadcrumbNavigation } from "ui/MobileBreadcrumbNavigation";
import { SectionHeading } from "ui/SectionHeading";
import { Stepper } from "ui/Stepper";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { ResaleApplicationAction } from "./ResaleApplicationAction";
import { ResaleApplicationDetails } from "./ResaleApplicationDetails";
import { ResaleApplicationPhoneVerification } from "./ResaleApplicationPhoneVerification";
import { ResaleApplicationQuantitySelection } from "./ResaleApplicationQuantitySelection";
import { ResaleEventSummary } from "./ResaleEventSummary";

import type { FC } from "react";

type Props = {
  applicationEndAt: number;
  eventId: string;
  eventOpenAt: number;
  eventStartAt: number;
  eventThumbnailUrl: string;
  eventTitle: string;
  lotteryDate: number;
  maxApplicableQuantity: number;
  roundNumber: number;
  selectedQuantity: number | null;
  ticketName: string;
  ticketPriceAmount: number;
  onQuantityChange: (quantity: number | null) => void;
  onSubmit: () => void;
};

export const ResaleApplication: FC<Props> = ({
  applicationEndAt,
  eventId,
  eventOpenAt,
  eventStartAt,
  eventThumbnailUrl,
  eventTitle,
  lotteryDate,
  maxApplicableQuantity,
  roundNumber,
  selectedQuantity,
  ticketName,
  ticketPriceAmount,
  onQuantityChange,
  onSubmit,
}) => {
  const { t, i18n } = useI18n();
  const classes = pageStyles();
  const isPhoneNumberVerified = true;
  const pageTitle = t("event.resale.entry.title");

  const weekdayFormatter = new Intl.DateTimeFormat(
    i18n.language.replace("_", "-"),
    { weekday: "short" }
  );
  const eventOpenAtDate = new Date(eventOpenAt);
  const eventDateTime = `${format(
    eventOpenAtDate,
    "yyyy/MM/dd"
  )} (${weekdayFormatter.format(eventOpenAtDate)}) ${format(
    eventOpenAtDate,
    "HH:mm"
  )} / ${format(new Date(eventStartAt), "HH:mm")}`;
  const applicationEndAtDate = new Date(applicationEndAt);
  const applicationEndAtWeekday = weekdayFormatter.format(applicationEndAtDate);
  const resaleCloseAt = `${format(
    applicationEndAtDate,
    "yyyy/MM/dd"
  )}（${applicationEndAtWeekday}）${format(applicationEndAtDate, "HH:mm")}`;
  const resaleResultAt = `${format(
    new Date(lotteryDate),
    "yyyy/MM/dd HH:mm"
  )} ${t("event.resale.after")}`;

  return (
    <SubContentsLayout>
      <PCView>
        <BreadcrumbNavigation
          breadcrumbs={[
            { label: t("common.routes.home"), link: "/" },
            { label: t("event.resale.entry.eventList"), link: "/events" },
            { label: eventTitle, link: `/events/${eventId}` },
            { label: pageTitle, link: null },
          ]}
        />
      </PCView>
      <SPView>
        <MobileBreadcrumbNavigation
          href={`/events/${eventId}`}
          label={pageTitle}
        />
      </SPView>

      <div css={classes.root}>
        <PCView>
          <h1 css={classes.heading}>{pageTitle}</h1>
        </PCView>
        <Stepper
          ariaLabel={t("event.resale.entry.steps.ariaLabel")}
          currentStepId="quantitySelection"
          steps={[
            {
              id: "quantitySelection",
              label: t("event.resale.entry.steps.quantity"),
            },
            {
              id: "confirmation",
              label: t("event.resale.entry.steps.confirmation"),
            },
            {
              id: "completion",
              label: t("event.resale.entry.steps.completion"),
            },
          ]}
        />

        <ResaleEventSummary
          eventDateTime={eventDateTime}
          eventThumbnailURL={eventThumbnailUrl}
          eventTitle={eventTitle}
        />

        <SectionHeading as="h2" fontSize={18} fontWeight={700}>
          {t("event.resale.entry.quantitySelectionTitle")}
        </SectionHeading>

        <ResaleApplicationQuantitySelection
          maxApplicableQuantity={maxApplicableQuantity}
          selectedQuantity={selectedQuantity}
          ticketName={ticketName}
          ticketPriceAmount={ticketPriceAmount}
          onChange={onQuantityChange}
        />

        <ResaleApplicationDetails
          resaleTitle={t("event.resale.entry.officialResale", { roundNumber })}
          resaleCloseAt={resaleCloseAt}
          resaleResultAt={resaleResultAt}
          paymentMethod={t("event.resale.entry.creditCardOnly")}
        />

        <ResaleApplicationPhoneVerification
          isPhoneNumberVerified={isPhoneNumberVerified}
          phoneNumber={"+81 90-****-0000"}
        />

        <ResaleApplicationAction
          isPhoneNumberVerified={isPhoneNumberVerified}
          isQuantitySelected={selectedQuantity !== null}
          onConfirmEntry={onSubmit}
        />
      </div>
    </SubContentsLayout>
  );
};

const pageStyles = () => ({
  root: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 92%;
    max-width: 760px;
    padding: 24px 0 0;
    margin: 0 auto;
    @media screen and (min-width: 768px) {
      gap: 32px;
      padding-top: 40px;
    }
  `,
  heading: css`
    font-size: 18px;
    font-weight: bold;
    line-height: 1.5;
    @media screen and (min-width: 768px) {
      font-size: 24px;
    }
  `,
});
